import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Globe, Check } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";

/** Best-effort persist of the chosen language to the per-user
 *  preference store. Fire-and-forget — a 401 (unauth visitor on the
 *  landing page) or a 503 (Mongo down) must never block the local
 *  language switch. iter139.5 — see /api/conv/me/lang. */
function persistLangBestEffort(code) {
  try {
    api.put("/conv/me/lang", { lang: code }).catch(() => {});
  } catch {
    /* ignore — the local switch already happened */
  }
}

/**
 * LanguagePicker — globe-icon dropdown locked to the three supported
 * languages (English / French / Spanish).
 *
 * The trigger button stays inline with the rest of the header; the
 * dropdown PANEL renders via `createPortal` into `document.body` so it
 * escapes any parent `backdrop-blur` / `transform` stacking context
 * that would otherwise clip it (this used to bite us on
 * `/admin/email-limits` and `/admin/test-company` where the sticky
 * header's `backdrop-blur-xl` stack-trapped the absolute-positioned
 * panel and made the picker look like a no-op).
 */
export default function LanguagePicker({ alignRight = true }) {
  const { lang, setLang, languages, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, right: 0 });
  const btnRef = useRef(null);

  const current = languages.find((l) => l.code === lang) ||
    { code: lang, native_name: lang.toUpperCase(), name: lang };

  // Recompute panel coords whenever it opens or the window scrolls /
  // resizes — we're rendering into the body, so positioning has to be
  // viewport-relative.
  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      setCoords({
        top: r.bottom + 4,
        left: r.left,
        right: window.innerWidth - r.right,
      });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  return (
    <div className="relative" data-testid="language-picker-root">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="language-picker-trigger"
        className="inline-flex items-center justify-center gap-1 sm:gap-1.5 h-8 w-8 sm:w-auto sm:px-2.5 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700"
        title={t("lang.label", "Language")}
      >
        <Globe size={14} weight="bold" />
        <span className="hidden sm:inline uppercase tracking-wider">{current.code}</span>
      </button>

      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-[200]"
            onClick={() => setOpen(false)}
            data-testid="language-picker-scrim"
          />
          <div
            className="fixed z-[201] w-56 rounded-md border border-slate-200 bg-white shadow-xl overflow-hidden"
            data-testid="language-picker-menu"
            style={alignRight
              ? { top: coords.top, right: coords.right }
              : { top: coords.top, left: coords.left }}
          >
            <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {t("lang.label", "Language")}
            </div>
            <div className="py-1">
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setLang(l.code); persistLangBestEffort(l.code); setOpen(false); }}
                  data-testid={`language-option-${l.code}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${l.code === lang ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700"}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase text-slate-400 w-6">{l.code}</span>
                    <span>{l.native_name || l.name}</span>
                  </span>
                  {l.code === lang && <Check size={14} weight="bold" />}
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
